import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Instagram, Mail, MessageSquare, ArrowUpRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HELP_OPTIONS = [
  "Website Redesign",
  "New Landing Page",
  "Booking & Contact Setup",
  "Website Audit / Review",
  "AI Chatbot Integration",
  "Not sure yet",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    business_name: "",
    email: "",
    phone: "",
    website: "",
    help_with: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, form);
      toast.success("Request sent — I'll be in touch shortly.");
      setSubmitted(true);
      setForm({
        name: "",
        business_name: "",
        email: "",
        phone: "",
        website: "",
        help_with: "",
        message: "",
      });
    } catch (err) {
      const detail =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-32 border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12 lg:gap-16">
        {/* Left: info */}
        <div className="lg:col-span-5">
          <span className="text-xs tracking-[0.22em] uppercase text-blue-400 font-medium">
            Contact
          </span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight font-semibold mt-4 leading-[1.1]">
            Let&apos;s build the <span className="italic">version of your site</span> that actually works.
          </h2>
          <p className="mt-6 text-white/60 text-[15px] md:text-base leading-relaxed max-w-md">
            Send a quick message — tell me about your business and what you&apos;re trying to fix. I&apos;ll reply with thoughts, ideas, or a free concept.
          </p>

          <div className="mt-10 space-y-4">
            <a
              data-testid="contact-instagram"
              href="https://instagram.com/almontesites"
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-4 p-4 rounded-xl border border-white/10 hover:border-white/25 hover:bg-white/[0.02] transition-all"
            >
              <span className="h-11 w-11 rounded-lg flex items-center justify-center border border-white/10 bg-white/[0.03] text-white group-hover:text-blue-300 group-hover:border-blue-500/40 transition-colors">
                <Instagram size={18} />
              </span>
              <div className="flex-1">
                <div className="text-[11px] uppercase tracking-[0.18em] text-white/40">
                  Instagram
                </div>
                <div className="text-white text-sm font-medium">
                  @almontesites
                </div>
              </div>
              <ArrowUpRight size={16} className="text-white/30 group-hover:text-white transition-colors" />
            </a>

            <a
              data-testid="contact-email"
              href="mailto:almontesites@gmail.com"
              className="group flex items-center gap-4 p-4 rounded-xl border border-white/10 hover:border-white/25 hover:bg-white/[0.02] transition-all"
            >
              <span className="h-11 w-11 rounded-lg flex items-center justify-center border border-white/10 bg-white/[0.03] text-white group-hover:text-blue-300 group-hover:border-blue-500/40 transition-colors">
                <Mail size={18} />
              </span>
              <div className="flex-1">
                <div className="text-[11px] uppercase tracking-[0.18em] text-white/40">
                  Email
                </div>
                <div className="text-white text-sm font-medium">
                  almontesites@gmail.com
                </div>
              </div>
              <ArrowUpRight size={16} className="text-white/30 group-hover:text-white transition-colors" />
            </a>

            <a
              data-testid="contact-phone"
              href="sms:+17863287686"
              className="group flex items-center gap-4 p-4 rounded-xl border border-white/10 hover:border-white/25 hover:bg-white/[0.02] transition-all"
            >
              <span className="h-11 w-11 rounded-lg flex items-center justify-center border border-white/10 bg-white/[0.03] text-white group-hover:text-blue-300 group-hover:border-blue-500/40 transition-colors">
                <MessageSquare size={18} />
              </span>
              <div className="flex-1">
                <div className="text-[11px] uppercase tracking-[0.18em] text-white/40">
                  Text
                </div>
                <div className="text-white text-sm font-medium">
                  786-328-7686
                </div>
              </div>
              <ArrowUpRight size={16} className="text-white/30 group-hover:text-white transition-colors" />
            </a>
          </div>
        </div>

        {/* Right: form */}
        <div className="lg:col-span-7">
          <form
            data-testid="contact-form"
            onSubmit={onSubmit}
            className="rounded-2xl border border-white/10 bg-[#0F0F0F] p-7 md:p-10"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Name *">
                <Input
                  data-testid="contact-input-name"
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Your full name"
                  className="bg-[#0A0A0A] border-white/15 text-white placeholder:text-white/35 h-12 rounded-md focus-visible:ring-blue-500"
                />
              </Field>
              <Field label="Business Name">
                <Input
                  data-testid="contact-input-business"
                  value={form.business_name}
                  onChange={update("business_name")}
                  placeholder="e.g. Apex MMA Academy"
                  className="bg-[#0A0A0A] border-white/15 text-white placeholder:text-white/35 h-12 rounded-md focus-visible:ring-blue-500"
                />
              </Field>
              <Field label="Email *">
                <Input
                  data-testid="contact-input-email"
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="you@business.com"
                  className="bg-[#0A0A0A] border-white/15 text-white placeholder:text-white/35 h-12 rounded-md focus-visible:ring-blue-500"
                />
              </Field>
              <Field label="Phone">
                <Input
                  data-testid="contact-input-phone"
                  value={form.phone}
                  onChange={update("phone")}
                  placeholder="(555) 555-5555"
                  className="bg-[#0A0A0A] border-white/15 text-white placeholder:text-white/35 h-12 rounded-md focus-visible:ring-blue-500"
                />
              </Field>
              <Field label="Website URL" className="md:col-span-2">
                <Input
                  data-testid="contact-input-website"
                  value={form.website}
                  onChange={update("website")}
                  placeholder="https://yourbusiness.com"
                  className="bg-[#0A0A0A] border-white/15 text-white placeholder:text-white/35 h-12 rounded-md focus-visible:ring-blue-500"
                />
              </Field>
              <Field label="What do you need help with?" className="md:col-span-2">
                <Select value={form.help_with} onValueChange={update("help_with")}>
                  <SelectTrigger
                    data-testid="contact-select-help"
                    className="bg-[#0A0A0A] border-white/15 text-white h-12 rounded-md focus:ring-blue-500"
                  >
                    <SelectValue placeholder="Choose a service" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0F0F0F] border-white/10 text-white">
                    {HELP_OPTIONS.map((opt) => (
                      <SelectItem
                        key={opt}
                        value={opt}
                        data-testid={`contact-help-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                      >
                        {opt}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Message" className="md:col-span-2">
                <Textarea
                  data-testid="contact-input-message"
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Tell me a bit about your business and what you want to fix..."
                  rows={5}
                  className="bg-[#0A0A0A] border-white/15 text-white placeholder:text-white/35 rounded-md focus-visible:ring-blue-500 resize-none"
                />
              </Field>
            </div>

            <div className="mt-7 flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-xs text-white/40">
                By submitting, you agree to be contacted about your request.
              </p>
              <Button
                data-testid="contact-submit-button"
                type="submit"
                disabled={submitting}
                className="bg-blue-500 hover:bg-blue-600 text-white rounded-full h-12 px-7 font-medium shadow-[0_10px_40px_-10px_rgba(59,130,246,0.6)] disabled:opacity-60"
              >
                {submitting ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    Send Request
                    <ArrowUpRight size={16} className="ml-1.5" />
                  </>
                )}
              </Button>
            </div>

            {submitted && (
              <div
                data-testid="contact-success-message"
                className="mt-6 rounded-lg border border-blue-500/30 bg-blue-500/5 p-4 text-sm text-blue-200"
              >
                Got it — your request was sent. I&apos;ll be in touch shortly.
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      <Label className="text-xs uppercase tracking-[0.18em] text-white/55 font-medium">
        {label}
      </Label>
      {children}
    </div>
  );
}
